import type {
  UseMutationOptions,
  UseQueryOptions,
} from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { z } from "zod";

import { RefreshClient, refreshAccessToken } from "@citrine/client";

import type { AgentEventMessage, AgentManifest } from "./agents";
import { readCachedManifest, writeCachedManifest } from "./utils";

const agentEventSchema = z.object({
  type: z.enum(["token", "event", "error"]),
  data: z.string(),
});

type AgentInvocationEvent = z.infer<typeof agentEventSchema>;

interface SendMessageRequest {
  abortSignal: AbortSignal;
  agentKey: string;
  context?: object;
  messages: AgentEventMessage[];
  onEvent: (event: AgentInvocationEvent) => void;
}

export class AgentsClient extends RefreshClient {
  private static instance: AgentsClient | undefined;

  protected readonly endpoint = "/api/v1/agents";

  protected constructor() {
    super();
  }

  static getInstance() {
    return (this.instance ??= new this());
  }

  invocationEndpoint(agentKey: string): string {
    return `${this.endpoint}/${agentKey}/invocations`;
  }

  async list(): Promise<AgentManifest[]> {
    const agents = await this.request<AgentManifest[]>({
      method: "GET",
      url: this.endpoint,
    });
    writeCachedManifest(agents);
    return agents;
  }

  useList(
    options: Omit<
      UseQueryOptions<AgentManifest[]>,
      "queryKey" | "queryFn"
    > = {},
  ) {
    // Seed from localStorage so returning users see the dropdown on first
    // paint; React Query revalidates in the background.
    const initialData = readCachedManifest();

    return useQuery({
      ...options,
      queryKey: ["agents"],
      queryFn: () => this.list(),
      staleTime: 5 * 60 * 1000, // matches shire's discovery cache TTL
      ...(initialData ? { initialData, initialDataUpdatedAt: 0 } : {}),
    });
  }

  async sendMessage({
    abortSignal,
    agentKey,
    context,
    messages,
    onEvent,
  }: SendMessageRequest) {
    const tokenResponse = await refreshAccessToken(true);

    let buffer = "";
    let consumed = 0;

    const handleChunk = (responseText: string) => {
      buffer += responseText.slice(consumed);
      consumed = responseText.length;

      const lines = buffer.split("\n");
      buffer = lines.pop() ?? ""; // Keep the trailing partial line

      for (const line of lines) {
        // Skip just this line, not the rest of the batch: every entry here is a
        // complete line (the trailing partial is held back in `buffer`), so a
        // blank or unparseable line is interior framing/garbage to step over.
        if (!line.trim()) {
          continue;
        }

        let parsed: unknown;
        try {
          parsed = JSON.parse(line);
        } catch {
          continue;
        }

        const result = agentEventSchema.safeParse(parsed);
        if (result.success) {
          onEvent(result.data);
        }
      }
    };

    await this.request({
      data: {
        auth_token: tokenResponse?.access_token,
        citrine_host: window.location.origin,
        messages,
        ...(context ? { context } : {}),
      },
      method: "POST",
      responseType: "text",
      signal: abortSignal,
      onDownloadProgress: (progressEvent) => {
        const xhr = progressEvent.event?.target;
        if (xhr instanceof XMLHttpRequest) {
          handleChunk(xhr.responseText);
        }
      },
      url: this.invocationEndpoint(agentKey),
    });
  }

  useSendMessage(
    options: Omit<
      UseMutationOptions<undefined, AxiosError, SendMessageRequest>,
      "mutationFn"
    > = {},
  ) {
    return useMutation({
      ...options,
      mutationFn: (data) => this.sendMessage(data),
    });
  }
}
